/* DailyLogPanel — one editable widget for food + workouts + weight,
   reused across Kitchen, Gym, Tracker, Calendar, AI Coach.

   All rows show source_hub as a subtle pill (kitchen_cook, food_photo,
   gym_session, manual_tracker, ...) so users understand where each
   entry came from.  Every field is inline-editable via ✏️ button.
   Adds retroactive-edit support via optional `datePt` prop (Tracker
   Calendar passes the selected day).

   Includes a 🎙️ Coach OMNI natural-language input: type "log 200g
   chicken" and the LLM parses it into entries via /api/ai-coach/log-chat.

   Props:
     datePt?: "YYYY-MM-DD"   — Pacific-time date. Omit for today.
     compact?: bool          — hide the quick-add form (Kitchen/Gym use it).
     title?: string          — custom header (defaults to "Today's Log").
*/
import React, { useState } from "react";
import axios from "axios";
import useDailyLog from "@/hooks/useDailyLog";
import { Button } from "@/components/ui/button";
import { Input }  from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SourcePill({ hub }) {
  const label = {
    kitchen_cook:    "🍳 Kitchen",
    kitchen_claim:   "🍽️ Kitchen",
    kitchen_session: "🍳 Kitchen",
    food_photo:      "📷 Photo",
    manual_tracker:  "✍️ Manual",
    quick_add:       "⚡ Quick",
    gym_session:     "🏋️ Gym",
    weekly_workout:  "📅 Weekly",
  }[hub] || hub || "—";
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60 uppercase tracking-wider" data-testid="daily-log-source-pill">
      {label}
    </span>
  );
}

function EditableCell({ value, onSave, type = "number", step, min, suffix, dataTestid }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");
  React.useEffect(() => { setV(value ?? ""); }, [value]);
  if (!editing) {
    return (
      <button
        className="inline-flex items-center gap-1 hover:bg-white/5 rounded px-1 py-0.5 text-sm"
        onClick={() => setEditing(true)}
        data-testid={dataTestid}
      >
        <span>{value === null || value === undefined || value === "" ? "—" : value}{suffix ? ` ${suffix}` : ""}</span>
        <span className="opacity-40 text-xs">✏️</span>
      </button>
    );
  }
  const commit = async () => {
    setEditing(false);
    let out = v;
    if (type === "number") out = Number(v) || 0;
    if (out !== value) await onSave(out);
  };
  return (
    <Input
      autoFocus
      type={type}
      value={v}
      step={step}
      min={min}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
      className="h-7 w-20 text-sm"
    />
  );
}

function FoodRow({ f, onPatch, onDelete }) {
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid={`daily-log-food-row-${f.entry_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <EditableCell
            value={f.item}
            type="text"
            onSave={(v) => onPatch({ item: String(v) })}
            dataTestid={`daily-log-food-item-${f.entry_id}`}
          />
          <SourcePill hub={f.source_hub} />
          {f.edited && <span className="text-[10px] text-amber-400/70">edited</span>}
        </div>
        <div className="flex items-center gap-3 text-xs text-white/70 mt-0.5 flex-wrap">
          <span>
            <EditableCell value={f.qty} step="0.1" min="0" onSave={(v) => onPatch({ qty: v })} dataTestid={`daily-log-food-qty-${f.entry_id}`} />
            <span className="ml-1 text-white/50">{f.unit}</span>
          </span>
          <span>🔥 <EditableCell value={f.kcal} onSave={(v) => onPatch({ kcal: v })} dataTestid={`daily-log-food-kcal-${f.entry_id}`} /> kcal</span>
          <span>P <EditableCell value={f.protein_g} onSave={(v) => onPatch({ protein_g: v })} dataTestid={`daily-log-food-p-${f.entry_id}`} />g</span>
          <span>C <EditableCell value={f.carbs_g} onSave={(v) => onPatch({ carbs_g: v })} dataTestid={`daily-log-food-c-${f.entry_id}`} />g</span>
          <span>F <EditableCell value={f.fat_g} onSave={(v) => onPatch({ fat_g: v })} dataTestid={`daily-log-food-f-${f.entry_id}`} />g</span>
        </div>
      </div>
      <button className="text-red-400/70 hover:text-red-400 text-lg leading-none px-1" onClick={onDelete} data-testid={`daily-log-food-delete-${f.entry_id}`} title="Delete">
        ×
      </button>
    </div>
  );
}

function WorkoutRow({ w, onPatch, onDelete }) {
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid={`daily-log-workout-row-${w.entry_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <EditableCell
            value={w.exercise}
            type="text"
            onSave={(v) => onPatch({ exercise: String(v) })}
            dataTestid={`daily-log-wo-name-${w.entry_id}`}
          />
          <SourcePill hub={w.source_hub} />
          {w.edited && <span className="text-[10px] text-amber-400/70">edited</span>}
        </div>
        <div className="flex items-center gap-3 text-xs text-white/70 mt-0.5 flex-wrap">
          <span>Sets <EditableCell value={w.sets} onSave={(v) => onPatch({ sets: v })} dataTestid={`daily-log-wo-sets-${w.entry_id}`} /></span>
          <span>Reps <EditableCell value={w.reps} onSave={(v) => onPatch({ reps: v })} dataTestid={`daily-log-wo-reps-${w.entry_id}`} /></span>
          <span>🏋️ <EditableCell value={w.weight_lbs} step="0.5" onSave={(v) => onPatch({ weight_lbs: v })} dataTestid={`daily-log-wo-wt-${w.entry_id}`} /> lb</span>
          <span>⏱️ <EditableCell value={w.duration_min} onSave={(v) => onPatch({ duration_min: v })} dataTestid={`daily-log-wo-min-${w.entry_id}`} /> min</span>
          <span>🔥 <EditableCell value={w.kcal_burned} onSave={(v) => onPatch({ kcal_burned: v })} dataTestid={`daily-log-wo-kcal-${w.entry_id}`} /> kcal</span>
        </div>
      </div>
      <button className="text-red-400/70 hover:text-red-400 text-lg leading-none px-1" onClick={onDelete} data-testid={`daily-log-wo-delete-${w.entry_id}`} title="Delete">
        ×
      </button>
    </div>
  );
}

function QuickAdd({ addFood, addWorkout }) {
  const [tab, setTab] = useState("food");
  const [fItem, setFItem] = useState("");
  const [fKcal, setFKcal] = useState("");
  const [fProt, setFProt] = useState("");
  const [wEx, setWEx] = useState("");
  const [wDur, setWDur] = useState("");
  const [wKcal, setWKcal] = useState("");

  const submitFood = async () => {
    if (!fItem.trim()) { toast.error("Item name required"); return; }
    await addFood({
      item: fItem.trim(),
      qty: 1, unit: "serving",
      kcal: Number(fKcal) || 0,
      protein_g: Number(fProt) || 0,
      source_hub: "quick_add",
    });
    setFItem(""); setFKcal(""); setFProt("");
    toast.success("Logged 🍽️");
  };

  const submitWorkout = async () => {
    if (!wEx.trim()) { toast.error("Exercise required"); return; }
    await addWorkout({
      exercise: wEx.trim(),
      duration_min: Number(wDur) || 0,
      kcal_burned: Number(wKcal) || 0,
      source_hub: "quick_add",
    });
    setWEx(""); setWDur(""); setWKcal("");
    toast.success("Logged 🏋️");
  };

  return (
    <div className="border-t border-white/5 pt-3 mt-3">
      <div className="flex gap-2 mb-2">
        <button
          className={`text-xs px-3 py-1 rounded-full ${tab === "food" ? "bg-emerald-500/20 text-emerald-200" : "bg-white/5 text-white/60"}`}
          onClick={() => setTab("food")}
          data-testid="daily-log-tab-food"
        >➕ Food</button>
        <button
          className={`text-xs px-3 py-1 rounded-full ${tab === "workout" ? "bg-orange-500/20 text-orange-200" : "bg-white/5 text-white/60"}`}
          onClick={() => setTab("workout")}
          data-testid="daily-log-tab-workout"
        >➕ Workout</button>
      </div>
      {tab === "food" ? (
        <div className="flex flex-wrap gap-2">
          <Input placeholder="Item" value={fItem} onChange={(e) => setFItem(e.target.value)} className="h-8 flex-1 min-w-[140px]" data-testid="daily-log-quickadd-item"/>
          <Input placeholder="kcal" type="number" value={fKcal} onChange={(e) => setFKcal(e.target.value)} className="h-8 w-20" data-testid="daily-log-quickadd-kcal"/>
          <Input placeholder="P g"  type="number" value={fProt} onChange={(e) => setFProt(e.target.value)} className="h-8 w-16" data-testid="daily-log-quickadd-prot"/>
          <Button size="sm" onClick={submitFood} data-testid="daily-log-quickadd-food-submit">Log</Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Input placeholder="Exercise" value={wEx} onChange={(e) => setWEx(e.target.value)} className="h-8 flex-1 min-w-[140px]" data-testid="daily-log-quickadd-exercise"/>
          <Input placeholder="min" type="number" value={wDur} onChange={(e) => setWDur(e.target.value)} className="h-8 w-16" data-testid="daily-log-quickadd-dur"/>
          <Input placeholder="kcal" type="number" value={wKcal} onChange={(e) => setWKcal(e.target.value)} className="h-8 w-20" data-testid="daily-log-quickadd-workout-kcal"/>
          <Button size="sm" onClick={submitWorkout} data-testid="daily-log-quickadd-workout-submit">Log</Button>
        </div>
      )}
    </div>
  );
}

// 🎙️ Coach OMNI natural-language log chat.
// Users type "log 200g chicken" and Claude parses it into structured
// food + workout entries via /api/ai-coach/log-chat.
function CoachLogChat({ datePt, onLogged }) {
  const [text, setText]   = useState("");
  const [busy, setBusy]   = useState(false);
  const [lastReply, setLastReply] = useState("");

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/ai-coach/log-chat`,
        { text: t, date_pt: datePt || undefined },
        { withCredentials: true },
      );
      setLastReply(data?.reply || "Logged.");
      setText("");
      const foods = (data?.foods_added || []).length;
      const wos   = (data?.workouts_added || []).length;
      if (foods || wos) {
        toast.success(
          `Coach OMNI logged ${foods ? `${foods} meal${foods > 1 ? "s" : ""}` : ""}${foods && wos ? " · " : ""}${wos ? `${wos} workout${wos > 1 ? "s" : ""}` : ""}`,
          { duration: 4000 }
        );
      } else {
        toast.info(data?.reply || "Nothing to log.");
      }
      // Bubble the change into any shared useDailyLog instance
      try { window.dispatchEvent(new CustomEvent("daily-log:changed", { detail: { date_pt: datePt || null } })); } catch { /* noop */ }
      onLogged?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coach OMNI couldn't parse that. Try more specific.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-white/5 pt-3 mt-3" data-testid="coach-log-chat">
      <div className="text-xs uppercase tracking-wider text-white/50 mb-1">
        🎙️ Ask Coach OMNI — natural language
      </div>
      <div className="flex gap-2">
        <Input
          placeholder='Try: "log 200g chicken breast and 3x10 push-ups"'
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          className="h-9 flex-1"
          disabled={busy}
          data-testid="coach-log-chat-input"
        />
        <Button size="sm" onClick={send} disabled={busy || !text.trim()} data-testid="coach-log-chat-submit">
          {busy ? "…" : "Log"}
        </Button>
      </div>
      {lastReply && (
        <div className="text-xs text-emerald-200/80 italic mt-2" data-testid="coach-log-chat-reply">
          🐦‍🔥 {lastReply}
        </div>
      )}
    </div>
  );
}

export default function DailyLogPanel({ datePt, compact = false, title }) {
  const {
    day, loading,
    addFood, addWorkout, setWeight,
    patchFood, patchWorkout, deleteFood, deleteWorkout,
  } = useDailyLog(datePt);

  const [wt, setWt] = useState("");
  React.useEffect(() => {
    setWt(day?.weight_lbs != null ? String(day.weight_lbs) : "");
  }, [day?.weight_lbs]);

  const totals = day?.totals || {};
  const heading = title || (datePt ? `Log · ${datePt}` : "Today's Log");

  return (
    <Card className="bg-white/[0.02] border-white/10" data-testid="daily-log-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">📊 {heading}</CardTitle>
          <div className="flex items-center gap-2 text-xs text-white/70">
            <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-200">
              🔥 {totals.kcal_in || 0} kcal in
            </Badge>
            <Badge variant="secondary" className="bg-orange-500/10 text-orange-200">
              🔥 {totals.kcal_out || 0} kcal out
            </Badge>
            <Badge variant="secondary" className="bg-white/5">
              P {totals.protein_g || 0} · C {totals.carbs_g || 0} · F {totals.fat_g || 0}
            </Badge>
            <Badge variant="secondary" className="bg-white/5">
              ⏱️ {totals.workout_min || 0} min
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Weight card */}
        <div className="flex items-center gap-2 text-sm bg-white/[0.03] rounded-lg px-3 py-2">
          <span className="text-white/70">⚖️ Weight</span>
          <Input
            type="number"
            step="0.1"
            value={wt}
            onChange={(e) => setWt(e.target.value)}
            onBlur={async () => {
              if (wt === "" || Number(wt) === day?.weight_lbs) return;
              await setWeight(wt);
              toast.success("Weight saved");
            }}
            className="h-8 w-24"
            data-testid="daily-log-weight-input"
          />
          <span className="text-white/50 text-xs">lbs</span>
        </div>

        {/* Foods */}
        <div>
          <div className="text-xs uppercase tracking-wider text-white/50 mb-1">🍽️ Food ({(day.foods || []).length})</div>
          {(day.foods || []).length === 0 ? (
            <div className="text-xs text-white/40 italic py-2" data-testid="daily-log-food-empty">Nothing logged yet — cook something, snap a food photo, or use ➕ Food below.</div>
          ) : (
            (day.foods || []).map((f) => (
              <FoodRow
                key={f.entry_id}
                f={f}
                onPatch={(patch) => patchFood(f.entry_id, patch)}
                onDelete={() => deleteFood(f.entry_id)}
              />
            ))
          )}
        </div>

        {/* Workouts */}
        <div>
          <div className="text-xs uppercase tracking-wider text-white/50 mb-1">🏋️ Workouts ({(day.workouts || []).length})</div>
          {(day.workouts || []).length === 0 ? (
            <div className="text-xs text-white/40 italic py-2" data-testid="daily-log-wo-empty">No workouts logged — hit the gym or use ➕ Workout below.</div>
          ) : (
            (day.workouts || []).map((w) => (
              <WorkoutRow
                key={w.entry_id}
                w={w}
                onPatch={(patch) => patchWorkout(w.entry_id, patch)}
                onDelete={() => deleteWorkout(w.entry_id)}
              />
            ))
          )}
        </div>

        {!compact && <QuickAdd addFood={addFood} addWorkout={addWorkout} />}
        {!compact && <CoachLogChat datePt={datePt} />}
        {loading && <div className="text-xs text-white/40">Loading…</div>}
      </CardContent>
    </Card>
  );
}
